import { config } from '../config/env';

interface FileValidation {
  valid: boolean;
  error?: string;
}

interface S3UploadResponse {
  success: boolean;
  url?: string;
  message?: string;
}

/**
 * Servicio para subir archivos a S3 a través del backend
 * El backend maneja las credenciales AWS y sube el archivo
 */
const s3ServiceImpl = {
  /**
   * Valida un archivo antes de subirlo
   */
  validateFile(file: File): FileValidation {
    // Validar tipo de archivo
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Solo se permiten archivos PDF o Word (.doc, .docx)'
      };
    }

    // Validar tamaño (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB en bytes
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'El archivo debe ser menor a 5MB'
      };
    }

    return { valid: true };
  },

  /**
   * Sube un archivo a S3 a través del backend
   * @param file - Archivo a subir
   * @param folder - Carpeta en S3 (por defecto 'cvs')
   */
  async uploadFile(file: File, folder: string = 'cvs'): Promise<S3UploadResponse> {
    try {
      // Crear FormData para enviar el archivo
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      // Enviar al backend
      const response = await fetch(`${config.apiBaseUrl}/upload/s3`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error al subir archivo' }));
        throw new Error(errorData.message || 'Error al subir el archivo');
      }

      const result = await response.json();
      
      return {
        success: result.success || true,
        url: result.url,
        message: result.message || 'Archivo subido exitosamente'
      };
    } catch (error) {
      console.error('Error al subir archivo:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al subir el archivo'
      };
    }
  },

  /**
   * Formatea el tamaño de archivo a formato legible
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
};

export { s3ServiceImpl as s3Service };
